class ScB2bRegistration extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.form = this.querySelector('[data-sc-b2b-form]');
    this.fileInput = this.querySelector('[data-sc-file-input]');
    this.fileControl = this.querySelector('[data-sc-file-control]');
    this.fileName = this.querySelector('[data-sc-file-name]');
    this.countrySelect = this.querySelector('[data-sc-country-select]');
    this.status = this.querySelector('[data-sc-status]');
    this.submitButton = this.querySelector('[data-sc-submit]');
    this.fields = Object.fromEntries(
      Array.from(this.querySelectorAll('[data-sc-field]')).map((field) => [field.dataset.scField, field])
    );
    this.validationFieldNames = [
      'first_name',
      'last_name',
      'email',
      'phone',
      'company',
      'business_proof',
      'street',
      'postal_code',
      'city',
      'country',
      'comment',
      'information_consent'
    ];
    this.fieldErrorElements = this.createFieldErrorElements();

    this.fileInput?.addEventListener('change', () => this.handleFileChange());
    this.setupFileDropZone();

    Object.values(this.fields).forEach((field) => {
      field.addEventListener('input', () => this.handleVisibleFieldInput(field));
      field.addEventListener('change', () => this.handleVisibleFieldInput(field));
    });

    this.form?.addEventListener('reset', () => {
      window.setTimeout(() => {
        this.resetFileState();
      }, 0);
    });
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  setupFileDropZone() {
    if (!this.fileControl || !this.fileInput) return;

    this.fileDragDepth = 0;
    this.fileControl.addEventListener('dragenter', (event) => this.handleFileDragEnter(event));
    this.fileControl.addEventListener('dragover', (event) => this.handleFileDragOver(event));
    this.fileControl.addEventListener('dragleave', (event) => this.handleFileDragLeave(event));
    this.fileControl.addEventListener('drop', (event) => this.handleFileDrop(event));
  }

  isFileDrag(event) {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }

  handleFileDragEnter(event) {
    if (!this.isFileDrag(event)) return;

    event.preventDefault();
    this.fileDragDepth += 1;
    this.fileControl.classList.add('is-dragover');
  }

  handleFileDragOver(event) {
    if (!this.isFileDrag(event)) return;

    event.preventDefault();

    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  handleFileDragLeave(event) {
    if (!this.isFileDrag(event)) return;

    this.fileDragDepth = Math.max(0, this.fileDragDepth - 1);

    if (this.fileDragDepth === 0) this.fileControl.classList.remove('is-dragover');
  }

  handleFileDrop(event) {
    if (!this.isFileDrag(event)) return;

    event.preventDefault();
    this.fileDragDepth = 0;
    this.fileControl.classList.remove('is-dragover');

    const file = event.dataTransfer?.files?.[0];

    if (!file) return;

    const transfer = new DataTransfer();
    transfer.items.add(file);
    this.fileInput.files = transfer.files;
    this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  normalizeLabel(value) {
    return String(value || '')
      .normalize('NFKC')
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase(document.documentElement.lang || 'de');
  }

  createFieldErrorElements() {
    return Object.fromEntries(this.validationFieldNames.map((fieldName) => {
      const field = this.fields[fieldName];
      const container = field?.closest('.sc-b2b-registration__field, .sc-b2b-registration__group');

      if (!field || !container) return [fieldName, null];

      const errorId = `${field.id}-error`;
      let error = container.querySelector(`[data-sc-field-error="${fieldName}"]`);

      if (!error) {
        error = document.createElement('p');
        error.id = errorId;
        error.className = 'sc-b2b-registration__field-error';
        error.dataset.scFieldError = fieldName;
        error.setAttribute('role', 'alert');
        error.hidden = true;

        if (field === this.fileInput) {
          const help = container.querySelector('.sc-b2b-registration__help');
          container.insertBefore(error, help || null);
        } else {
          container.append(error);
        }
      }

      field.setAttribute('aria-errormessage', errorId);
      return [fieldName, error];
    }));
  }

  getValidationMessage(type) {
    const messages = {
      required: this.dataset.scRequiredError,
      email: this.dataset.scEmailError,
      phone: this.dataset.scPhoneError,
      postal: this.dataset.scPostalError,
      unavailable: this.dataset.scUnavailableError,
      field: this.dataset.scFieldError,
      summary: this.dataset.scValidationSummary
    };

    return messages[type] || messages.field || '';
  }

  handleVisibleFieldInput(field) {
    if (field === this.fileInput) return;

    this.clearFieldError(field.dataset.scField);
    this.updateValidationSummary();
  }

  handleFileChange() {
    const file = this.fileInput?.files?.[0];
    const validationMessage = file ? this.validateFile(file) : '';

    if (validationMessage) {
      this.setFieldError('business_proof', validationMessage);
    } else {
      this.clearFieldError('business_proof');
    }

    this.updateValidationSummary();

    if (this.fileName) {
      this.fileName.textContent = file?.name || this.dataset.scFileEmptyLabel || '';
    }

    return !validationMessage;
  }

  validateFile(file) {
    const maxSizeMb = Number(this.fileInput?.dataset.scMaxSize || 0);
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    const acceptedTypes = String(this.fileInput?.accept || '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);

    if (maxSizeBytes > 0 && file.size > maxSizeBytes) {
      return this.dataset.scFileSizeError || '';
    }

    if (acceptedTypes.length && !this.isAcceptedFile(file, acceptedTypes)) {
      return this.dataset.scFileTypeError || '';
    }

    return '';
  }

  isAcceptedFile(file, acceptedTypes) {
    const fileName = String(file.name || '').toLowerCase();
    const mimeType = String(file.type || '').toLowerCase();

    return acceptedTypes.some((acceptedType) => {
      if (acceptedType.startsWith('.')) return fileName.endsWith(acceptedType);
      if (acceptedType.endsWith('/*')) return mimeType.startsWith(acceptedType.slice(0, -1));
      return mimeType === acceptedType;
    });
  }

  resetFileState() {
    this.clearFieldError('business_proof');

    if (this.fileName) {
      this.fileName.textContent = this.dataset.scFileEmptyLabel || '';
    }
  }

  setFieldError(fieldName, message) {
    const field = this.fields[fieldName];
    const error = this.fieldErrorElements?.[fieldName];

    if (!field || !message) return;

    field.setCustomValidity(message);
    field.setAttribute('aria-invalid', 'true');
    field.closest('.sc-b2b-registration__field, .sc-b2b-registration__group')?.classList.add('has-error');

    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  clearFieldError(fieldName) {
    const field = this.fields[fieldName];
    const error = this.fieldErrorElements?.[fieldName];

    if (!field) return;

    field.setCustomValidity('');
    field.removeAttribute('aria-invalid');
    field.closest('.sc-b2b-registration__field, .sc-b2b-registration__group')?.classList.remove('has-error');

    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  clearAllFieldErrors() {
    this.validationFieldNames.forEach((fieldName) => this.clearFieldError(fieldName));
  }

  hasFieldErrors() {
    return this.validationFieldNames.some((fieldName) => {
      return this.fields[fieldName]?.getAttribute('aria-invalid') === 'true';
    });
  }

  updateValidationSummary() {
    if (this.hasFieldErrors()) {
      this.setStatus(this.getValidationMessage('summary'), 'error');
    } else {
      this.clearErrorStatus();
    }
  }

  validateVisibleForm() {
    this.clearAllFieldErrors();

    const requiredFields = [
      'first_name',
      'last_name',
      'email',
      'phone',
      'company',
      'street',
      'postal_code',
      'city',
      'country'
    ];
    const errors = {};
    const getValue = (fieldName) => String(this.fields[fieldName]?.value || '').trim();

    requiredFields.forEach((fieldName) => {
      const value = getValue(fieldName);
      const isEmptyCountry = fieldName === 'country' && (!value || value === '---');

      if (!value || isEmptyCountry) errors[fieldName] = this.getValidationMessage('required');
    });

    const email = getValue('email');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      errors.email = this.getValidationMessage('email');
    }

    const phone = getValue('phone');
    const phoneDigits = phone.replace(/\D/g, '');
    if (phone && (!/^\+?[0-9\s().\/-]+$/.test(phone) || phoneDigits.length < 6 || phoneDigits.length > 15)) {
      errors.phone = this.getValidationMessage('phone');
    }

    const postalCode = getValue('postal_code');
    if (
      postalCode
      && (
        postalCode.length < 2
        || postalCode.length > 20
        || !/^[\p{L}\p{N}][\p{L}\p{N}\s-]*$/u.test(postalCode)
      )
    ) {
      errors.postal_code = this.getValidationMessage('postal');
    }

    this.validationFieldNames.forEach((fieldName) => {
      const field = this.fields[fieldName];
      if (!field || field === this.fileInput || !field.maxLength || field.maxLength < 0) return;
      if (String(field.value || '').length > field.maxLength) errors[fieldName] = this.getValidationMessage('field');
    });

    const file = this.fileInput?.files?.[0];
    if (file) {
      const fileError = this.validateFile(file);
      if (fileError) errors.business_proof = fileError;
    }

    Object.entries(errors).forEach(([fieldName, message]) => this.setFieldError(fieldName, message));

    if (Object.keys(errors).length) {
      this.setStatus(this.getValidationMessage('summary'), 'error');
      this.focusFirstInvalidField();
      return false;
    }

    this.clearErrorStatus();
    return true;
  }

  focusFirstInvalidField() {
    const fieldName = this.validationFieldNames.find((name) => {
      return this.fields[name]?.getAttribute('aria-invalid') === 'true';
    });

    this.fields[fieldName]?.focus({ preventScroll: false });
  }

  async handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.form || this.form.dataset.scSubmitting === 'true') return;

    if (!this.validateVisibleForm()) return;

    const values = this.getVisibleValues();

    this.form.dataset.scSubmitting = 'true';
    this.setStatus('', '');
    this.toggleSubmitting(true);

    try {
      const bridge = await this.waitForShopifyFormsBridge();

      this.validateShopifyFormsBridge(bridge);
      await this.setShopifyFormsCountryValue(bridge, values.countryLabel);
      this.refreshShopifyFormsBridge(bridge);
      this.validateShopifyFormsBridge(bridge);
      await this.populateShopifyFormsBridge(bridge, values);
      this.submitShopifyFormsBridge(bridge);
      await this.waitForShopifyFormsResult(bridge);

      this.showSuccessMessage(this.dataset.scSuccessMessage);
    } catch (error) {
      console.error('[sc-b2b-registration] Shopify Forms bridge submit failed', error);

      const hasFieldError = this.applyShopifyFormsFieldErrors(error);
      const bridgeUnavailable = /bridge.+(not found|not mounted|missing)/i.test(error?.message || '');

      if (!hasFieldError) {
        this.setStatus(
          bridgeUnavailable
            ? this.dataset.scNotConnectedMessage
            : this.dataset.scErrorMessage,
          'error'
        );
      }
    } finally {
      this.form.dataset.scSubmitting = 'false';
      this.toggleSubmitting(false);
    }
  }

  getVisibleValues() {
    return {
      firstName: this.fields.first_name?.value.trim() || '',
      lastName: this.fields.last_name?.value.trim() || '',
      email: this.fields.email?.value.trim() || '',
      phone: this.fields.phone?.value.trim() || '',
      company: this.fields.company?.value.trim() || '',
      file: this.fileInput?.files?.[0] || null,
      street: this.fields.street?.value.trim() || '',
      postalCode: this.fields.postal_code?.value.trim() || '',
      city: this.fields.city?.value.trim() || '',
      countryLabel: this.countrySelect?.selectedOptions?.[0]?.textContent?.trim() || '',
      comment: this.fields.comment?.value.trim() || '',
      informationConsent: Boolean(this.fields.information_consent?.checked)
    };
  }

  showSuccessMessage(message) {
    this.setStatus(message, 'success');
    this.form?.reset();

    window.requestAnimationFrame(() => {
      this.status?.setAttribute('tabindex', '-1');
      this.status?.focus({ preventScroll: true });
      this.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  }

  getSectionScope() {
    return this.closest('.sc-b2b-registration-section') || this.closest('.shopify-section');
  }

  getShopifyFormsFields(root) {
    const countryValue = root?.getElementById('shipping_address_country-selector-value');

    return {
      firstNameInput: root?.getElementById('first_name'),
      lastNameInput: root?.getElementById('last_name'),
      emailInput: root?.getElementById('email'),
      phoneInput: root?.getElementById('phone_number'),
      companyInput: root?.getElementById('company_name'),
      shippingCountryButton: countryValue?.closest('button'),
      shippingFirstNameInput: root?.getElementById('shipping_address_first_name'),
      shippingLastNameInput: root?.getElementById('shipping_address_last_name'),
      shippingRecipientInput: root?.getElementById('shipping_address_recipient'),
      shippingAddress1Input: root?.getElementById('shipping_address_address1'),
      shippingAddress2Input: root?.getElementById('shipping_address_address2'),
      shippingZipInput: root?.getElementById('shipping_address_zip'),
      shippingCityInput: root?.getElementById('shipping_address_city'),
      shippingPhoneInput: root?.getElementById('shipping_address_phone'),
      informationConsentInput: root?.querySelector('input[type="checkbox"][name="custom#einwilligung_infomaterial_1"]')
        || root?.getElementById('custom#einwilligung_infomaterial_1-Einwilligung Infomaterial'),
      fileInput: root?.getElementById('custom#gewerbenachweis-field'),
      commentInput: root?.querySelector('textarea[name="custom#kommentar"]')
    };
  }

  waitForShopifyFormsBridge(timeout = 15000) {
    const startedAt = Date.now();

    return new Promise((resolve, reject) => {
      const findBridge = () => {
        const scope = this.getSectionScope();
        const embed = scope?.querySelector('[data-sc-app-bridge] shopify-forms-embed#app-embed')
          || scope?.querySelector('[data-sc-app-bridge] shopify-forms-embed');
        const root = embed?.shadowRoot;
        const appRoot = root?.querySelector('#app-embed[data-current-step]')
          || root?.querySelector('[data-current-step]');
        const form = root?.querySelector('form[data-testid="form"]');
        const submitButton = root?.querySelector('[data-testid="btn-form-submit"]')
          || form?.querySelector('button[type="submit"], input[type="submit"]');
        const fields = this.getShopifyFormsFields(root);

        if (scope && embed && root && appRoot && form && submitButton && this.hasAllShopifyFormsFields(fields)) {
          return { scope, embed, root, appRoot, form, submitButton, ...fields };
        }

        return null;
      };

      const tick = () => {
        const bridge = findBridge();

        if (bridge) {
          resolve(bridge);
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          reject(new Error('Shopify Forms B2B bridge was not found or mounted in time.'));
          return;
        }

        window.setTimeout(tick, 150);
      };

      tick();
    });
  }

  hasAllShopifyFormsFields(fields) {
    return Object.values(fields).every(Boolean);
  }

  refreshShopifyFormsBridge(bridge) {
    Object.assign(bridge, this.getShopifyFormsFields(bridge.root));
    bridge.appRoot = bridge.root.querySelector('#app-embed[data-current-step]')
      || bridge.root.querySelector('[data-current-step]')
      || bridge.appRoot;
    bridge.form = bridge.root.querySelector('form[data-testid="form"]') || bridge.form;
    bridge.submitButton = bridge.root.querySelector('[data-testid="btn-form-submit"]')
      || bridge.form?.querySelector('button[type="submit"], input[type="submit"]')
      || bridge.submitButton;
  }

  validateShopifyFormsBridge(bridge) {
    if (!bridge.scope || !bridge.embed || !bridge.scope.contains(bridge.embed)) {
      throw new Error('Shopify Forms B2B bridge is not mounted inside the registration section.');
    }

    const fields = this.getShopifyFormsFields(bridge.root);

    if (!this.hasAllShopifyFormsFields(fields)) {
      throw new Error('One or more Shopify Forms B2B fields are missing.');
    }

    if (
      !bridge.form
      || !bridge.submitButton
      || bridge.submitButton.disabled
      || bridge.submitButton.getAttribute('aria-disabled') === 'true'
    ) {
      throw new Error('Shopify Forms B2B submit button is missing or unavailable.');
    }
  }

  async populateShopifyFormsBridge(bridge, values) {
    this.setNativeValue(bridge.firstNameInput, values.firstName);
    this.setNativeValue(bridge.lastNameInput, values.lastName);
    this.setNativeValue(bridge.emailInput, values.email);
    this.setNativeValue(bridge.companyInput, values.company);

    this.setNativeValue(bridge.shippingFirstNameInput, values.firstName);
    this.setNativeValue(bridge.shippingLastNameInput, values.lastName);
    this.setNativeValue(bridge.shippingRecipientInput, values.company);
    this.setNativeValue(bridge.shippingAddress1Input, values.street);
    this.setNativeValue(bridge.shippingAddress2Input, '');
    this.setNativeValue(bridge.shippingZipInput, values.postalCode);
    this.setNativeValue(bridge.shippingCityInput, values.city);
    this.setNativeValue(bridge.commentInput, values.comment);
    this.setNativeChecked(bridge.informationConsentInput, values.informationConsent);
    if (values.file) this.setNativeFile(bridge.fileInput, values.file);

    await this.setShopifyFormsPhoneValue(bridge.phoneInput, values.phone);
    await this.setShopifyFormsPhoneValue(bridge.shippingPhoneInput, values.phone);
  }

  setNativeValue(input, value) {
    if (!input) return;

    const prototype = input instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');

    if (descriptor?.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }

    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  setNativeChecked(input, checked) {
    if (!input || input.checked === checked) return;

    input.click();

    if (input.checked !== checked) {
      throw new Error('Shopify Forms did not synchronize the information consent checkbox.');
    }
  }

  setNativeFile(input, file) {
    if (!input || !file) return;

    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  async setShopifyFormsPhoneValue(input, value, timeout = 1800) {
    const phoneValue = String(value || '').trim().replace(/^00/, '+');

    if (!phoneValue) {
      this.setNativeValue(input, '');
      return;
    }

    if (!phoneValue.startsWith('+')) {
      this.setNativeValue(input, phoneValue);
      return;
    }

    let pasteEvent;

    try {
      const clipboardData = new DataTransfer();
      clipboardData.setData('text/plain', phoneValue);
      pasteEvent = new ClipboardEvent('paste', {
        bubbles: true,
        cancelable: true,
        composed: true,
        clipboardData
      });
    } catch (error) {
      pasteEvent = new Event('paste', {
        bubbles: true,
        cancelable: true,
        composed: true
      });
      Object.defineProperty(pasteEvent, 'clipboardData', {
        value: { getData: () => phoneValue }
      });
    }

    const pasteWasHandled = !input.dispatchEvent(pasteEvent);

    if (!pasteWasHandled) {
      throw new Error('Shopify Forms did not handle the international phone number paste.');
    }

    const expectedDigits = phoneValue.replace(/\D/g, '');
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeout) {
      const currentDigits = String(input.value || '').replace(/\D/g, '');

      if (currentDigits === expectedDigits) return;
      await this.delay(25);
    }

    throw new Error('Shopify Forms did not synchronize the international phone number.');
  }

  async setShopifyFormsCountryValue(bridge, countryLabel, timeout = 2000) {
    const expectedLabel = this.normalizeLabel(countryLabel);

    if (!expectedLabel) throw new Error('The selected shipping country is missing.');

    const getCountryButton = () => bridge.root
      .getElementById('shipping_address_country-selector-value')
      ?.closest('button');
    const getCurrentLabel = () => {
      const button = getCountryButton();
      const value = bridge.root.getElementById('shipping_address_country-selector-value');
      return this.normalizeLabel(value?.textContent || button?.getAttribute('aria-label'));
    };

    if (getCurrentLabel() === expectedLabel) return;

    const button = getCountryButton();
    if (!button) throw new Error('Shopify Forms shipping country selector is missing.');

    button.click();

    const option = await this.waitForCondition(() => {
      return Array.from(bridge.root.querySelectorAll('[role="option"]')).find((item) => {
        return this.normalizeLabel(item.textContent) === expectedLabel;
      });
    }, timeout);

    if (!option) {
      button.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, composed: true }));
      throw new Error(`Shopify Forms has no shipping country option matching "${countryLabel}".`);
    }

    option.click();

    const synchronized = await this.waitForCondition(
      () => getCurrentLabel() === expectedLabel,
      timeout
    );

    if (!synchronized) {
      throw new Error('Shopify Forms did not synchronize the shipping country.');
    }
  }

  waitForCondition(callback, timeout = 2000, interval = 25) {
    const startedAt = Date.now();

    return new Promise((resolve) => {
      const tick = () => {
        const result = callback();

        if (result) {
          resolve(result);
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          resolve(null);
          return;
        }

        window.setTimeout(tick, interval);
      };

      tick();
    });
  }

  delay(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  submitShopifyFormsBridge(bridge) {
    if (bridge.form.requestSubmit) {
      bridge.form.requestSubmit(bridge.submitButton);
      return;
    }

    bridge.submitButton.click();
  }

  waitForShopifyFormsResult(bridge, timeout = 30000) {
    const startedAt = Date.now();
    const errorSettleDelay = 900;

    return new Promise((resolve, reject) => {
      let observer = null;
      let timeoutId = null;
      let errorCheckId = null;
      let completed = false;
      let firstErrorSeenAt = 0;
      let pendingFieldErrors = {};

      const cleanup = () => {
        completed = true;
        observer?.disconnect();
        if (timeoutId) window.clearTimeout(timeoutId);
        if (errorCheckId) window.clearTimeout(errorCheckId);
      };

      const finish = (error) => {
        if (completed) return true;
        cleanup();
        if (error) reject(error);
        else resolve();
        return true;
      };

      const scheduleErrorRecheck = () => {
        if (errorCheckId) return;
        const elapsed = Date.now() - firstErrorSeenAt;
        const delay = Math.max(50, errorSettleDelay - elapsed + 25);
        errorCheckId = window.setTimeout(() => {
          errorCheckId = null;
          checkState();
        }, delay);
      };

      const checkState = () => {
        if (completed) return true;

        const currentAppRoot = bridge.root.querySelector('#app-embed[data-current-step]')
          || bridge.root.querySelector('[data-current-step]')
          || bridge.appRoot;

        if (currentAppRoot?.getAttribute('data-current-step') === 'success') return finish();

        const fieldErrors = this.collectShopifyFormsFieldErrors(bridge);

        if (Object.keys(fieldErrors).length) {
          pendingFieldErrors = fieldErrors;
          firstErrorSeenAt = firstErrorSeenAt || Date.now();

          if (Date.now() - firstErrorSeenAt >= errorSettleDelay) {
            const error = new Error('Shopify Forms reported validation errors.');
            error.fieldErrors = pendingFieldErrors;
            return finish(error);
          }

          scheduleErrorRecheck();
          return false;
        }

        firstErrorSeenAt = 0;
        pendingFieldErrors = {};

        if (Date.now() - startedAt >= timeout) {
          return finish(new Error('Shopify Forms did not report a submit result in time.'));
        }

        return false;
      };

      if (checkState()) return;

      observer = new MutationObserver(() => checkState());
      observer.observe(bridge.root, { attributes: true, childList: true, subtree: true });
      timeoutId = window.setTimeout(() => checkState(), timeout + 50);
    });
  }

  collectShopifyFormsFieldErrors(bridge) {
    const invalidFields = Array.from(
      bridge.root.querySelectorAll(
        'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"], button[aria-invalid="true"]'
      )
    );

    const fieldErrors = invalidFields.reduce((errors, field) => {
      const fieldName = this.getShopifyFormsFieldName(field);

      if (fieldName) errors[fieldName] = this.getShopifyFormsFieldErrorMessage(field) || '';
      return errors;
    }, {});
    const generalErrors = [];
    const errorElements = Array.from(
      bridge.root.querySelectorAll('[role="alert"], [data-testid*="error"]')
    );

    errorElements.forEach((errorElement) => {
      const text = errorElement.textContent?.trim();
      if (!text) return;

      const container = errorElement.closest(
        '[class*="formFieldContainer"], [class*="formPhoneInputContainer"], [class*="formFileInput"], [class*="selectContainer"], [class*="checkboxFieldSet"]'
      );
      const field = container?.querySelector('input, textarea, select, button');
      const fieldName = this.getShopifyFormsFieldName(field);

      if (fieldName) {
        if (!Object.prototype.hasOwnProperty.call(fieldErrors, fieldName)) fieldErrors[fieldName] = text;
      } else {
        generalErrors.push(text);
      }
    });

    if (generalErrors.length) fieldErrors.general = [...new Set(generalErrors)].join(' ');

    return fieldErrors;
  }

  getShopifyFormsFieldName(field) {
    if (!field) return '';

    const fieldName = field.name || field.id || field.dataset.testid?.replace(/^field-/, '');

    if (fieldName) return fieldName;
    if (field.querySelector?.('#shipping_address_country-selector-value')) return 'shipping_address_country';
    return '';
  }

  getShopifyFormsFieldErrorMessage(field) {
    const describedBy = field.getAttribute('aria-describedby');

    if (describedBy) {
      const root = field.getRootNode();

      for (const id of describedBy.split(/\s+/).filter(Boolean)) {
        const text = root?.getElementById?.(id)?.textContent?.trim();
        if (text) return text;
      }
    }

    const container = field.closest(
      '[class*="formFieldContainer"], [class*="formPhoneInputContainer"], [class*="formFileInput"], [class*="selectContainer"], [class*="checkboxFieldSet"]'
    );
    const error = container?.querySelector('[role="alert"], [data-testid*="error"], [class*="error"], [class*="Error"]');

    return error?.textContent?.trim() || '';
  }

  applyShopifyFormsFieldErrors(error) {
    const fieldErrors = error?.fieldErrors || {};
    const mappings = [
      { keys: ['first_name', 'shipping_address_first_name'], fieldName: 'first_name' },
      { keys: ['last_name', 'shipping_address_last_name'], fieldName: 'last_name' },
      { keys: ['email'], fieldName: 'email' },
      { keys: ['phone_number', 'shipping_address_phone'], fieldName: 'phone' },
      { keys: ['company_name', 'shipping_address_recipient'], fieldName: 'company' },
      { keys: ['shipping_address_address1', 'shipping_address_address2'], fieldName: 'street' },
      { keys: ['shipping_address_zip'], fieldName: 'postal_code' },
      { keys: ['shipping_address_city'], fieldName: 'city' },
      { keys: ['shipping_address_country'], fieldName: 'country' },
      { keys: ['custom#kommentar'], fieldName: 'comment' },
      {
        keys: [
          'custom#einwilligung_infomaterial_1',
          'custom#einwilligung_infomaterial_1-Einwilligung Infomaterial'
        ],
        fieldName: 'information_consent'
      }
    ];
    let hasMappedError = false;

    mappings.forEach((mapping) => {
      const key = mapping.keys.find((item) => Object.prototype.hasOwnProperty.call(fieldErrors, item));

      if (key && this.fields[mapping.fieldName]) {
        const message = this.translateShopifyFormsFieldError(mapping.fieldName, fieldErrors[key]);
        this.setFieldError(mapping.fieldName, message);
        hasMappedError = true;
      }
    });

    const fileKey = ['custom#gewerbenachweis-field', 'custom#gewerbenachweis']
      .find((item) => Object.prototype.hasOwnProperty.call(fieldErrors, item));

    if (fileKey && this.fileInput) {
      this.setFieldError(
        'business_proof',
        this.translateShopifyFormsFieldError('business_proof', fieldErrors[fileKey])
      );
      hasMappedError = true;
    }

    if (hasMappedError) {
      this.setStatus(this.getValidationMessage('summary'), 'error');
      this.focusFirstInvalidField();
      return true;
    }

    if (fieldErrors.general) {
      this.setStatus(this.dataset.scErrorMessage, 'error');
      return true;
    }

    return false;
  }

  translateShopifyFormsFieldError(fieldName, appMessage) {
    const message = String(appMessage || '').toLocaleLowerCase(document.documentElement.lang || 'de');
    const value = String(this.fields[fieldName]?.value || '').trim();

    if (/(already|taken|exists|unavailable|bereits|verwendet|vergeben)/i.test(message)) {
      return this.getValidationMessage('unavailable');
    }

    const isEmptyCheckbox = fieldName === 'information_consent' && !this.fields[fieldName]?.checked;
    const isEmptyFile = fieldName === 'business_proof' && !this.fileInput?.files?.length;
    const appReportsRequired = /(required|erforderlich|pflicht)/i.test(message);

    if (
      isEmptyCheckbox
      || isEmptyFile
      || (!value && (this.fields[fieldName]?.required || appReportsRequired))
    ) {
      return this.getValidationMessage('required');
    }

    if (fieldName === 'email') return this.getValidationMessage('email');
    if (fieldName === 'phone') return this.getValidationMessage('phone');
    if (fieldName === 'postal_code') return this.getValidationMessage('postal');
    if (fieldName === 'country') return this.getValidationMessage('required');

    return this.getValidationMessage('field');
  }

  clearErrorStatus() {
    if (this.status?.classList.contains('is-error')) this.setStatus('', '');
  }

  setStatus(message, type) {
    if (!this.status) return;

    const hasMessage = Boolean(message);

    this.status.textContent = message;
    this.status.hidden = !hasMessage;
    this.status.classList.toggle('is-error', type === 'error');
    this.status.classList.toggle('is-success', type === 'success');
    this.classList.toggle('is-success', hasMessage && type === 'success');
  }

  toggleSubmitting(isSubmitting) {
    if (!this.submitButton) return;

    this.submitButton.disabled = isSubmitting;
    this.submitButton.classList.toggle('is-loading', isSubmitting);
    this.submitButton.setAttribute('aria-busy', String(isSubmitting));
  }
}

if (!customElements.get('sc-b2b-registration')) {
  customElements.define('sc-b2b-registration', ScB2bRegistration);
}
